import { useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Plus, RefreshCw, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

import {
  EmptyRow,
  ErrorRow,
  LoadingRows,
  TablePager,
  TableToolbar,
  usePagedState,
} from "@/components/data-table";
import { TripProgress } from "@/components/trip-progress";
import { PageHeader } from "@/components/page-header";
import { StatusBadge, humanizeStatus } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NativeSelect } from "@/components/ui/native-select";
import { Input } from "@/components/ui/input";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type CarrierTrip,
  useArchiveCarrierTrip,
  useCarrierTrips,
  useDeleteCarrierTrip,
  useStartCarrierTrip,
  useUnassignTripOperator,
} from "@/api/carrier";
import { formatDate } from "@/lib/format";
import { hasHttpStatus } from "@/lib/api-error";
import { operatorDisplayName } from "@/lib/operator-identity";
import { AssignOperatorDialog } from "@/features/assign-operator-dialog";
import { AssignTasksDialog } from "@/features/assign-tasks-dialog";
import { AssignVehicleDialog } from "@/features/assign-vehicle-dialog";
import { CreateTripDialog } from "@/features/create-trip-dialog";
import { EditTripColorDialog } from "@/features/edit-trip-color-dialog";
import {
  TripDetailDialog,
  type TripColorTarget,
  type TripDeleteTarget,
  type TripVehicleTarget,
} from "@/features/trip-detail-dialog";

const COLUMNS = 10;

// TripStatus enum — the list isn't server-paginated, so we filter in memory.
const STATUS_FILTERS = ["PLANNED", "ACTIVE", "COMPLETED", "ARCHIVED"] as const;

/**
 * Trips list for the carrier portal. Carrier scope, status filtering, and
 * pagination are server-side; search remains a page-local convenience because
 * trips do not expose a server q parameter yet.
 */
function driverName(trip: CarrierTrip): string {
  return trip.operator
    ? operatorDisplayName(trip.operator)
    : "Unassigned operator";
}

function vehicleLabel(trip: CarrierTrip): string {
  if (!trip.vehicle) return "No vehicle";
  return [trip.vehicle.code, trip.vehicle.make, trip.vehicle.model]
    .filter(Boolean)
    .join(" · ");
}

function dispatchCenterName(trip: CarrierTrip): string {
  return (
    trip.dispatchCenter?.name ?? trip.depot?.name ?? "Unknown Dispatch Center"
  );
}

function tripDate(trip: CarrierTrip): string | null | undefined {
  return trip.startTime ?? trip.createdAt ?? trip.endTime;
}

function tripDateLabel(trip: CarrierTrip): string {
  const value = tripDate(trip);
  return value ? formatDate(value) : "Not scheduled";
}

export default function TripsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { page, pageSize, setPage, setPageSize, resetPage } = usePagedState();
  const { data, isLoading, error, refetch } = useCarrierTrips({
    page,
    limit: pageSize,
    ...(status && { status }),
    ...(search && { q: search }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
  });
  const archive = useArchiveCarrierTrip();
  const unassign = useUnassignTripOperator();
  const startTrip = useStartCarrierTrip();
  const deleteTrip = useDeleteCarrierTrip();
  const [assignTrip, setAssignTrip] = useState<CarrierTrip | null>(null);
  const [vehicleTrip, setVehicleTrip] = useState<TripVehicleTarget | null>(
    null
  );
  const [creating, setCreating] = useState(false);
  const [colorTrip, setColorTrip] = useState<TripColorTarget | null>(null);
  const [deletingTrip, setDeletingTrip] = useState<TripDeleteTarget | null>(
    null
  );
  const [detailTripId, setDetailTripId] = useState<string | null>(() =>
    searchParams.get("tripId")
  );
  const [assignTasksTripId, setAssignTasksTripId] = useState<string | null>(
    null
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const actionLocks = useRef(new Set<string>());

  const rows = data?.data ?? [];
  const total = data?.metadata.total ?? 0;
  const totalPages = Math.max(1, data?.metadata.totalPages ?? 1);

  const showRows = !isLoading && Boolean(data);
  const selectedTrip = (data?.data ?? []).find(
    (trip) => trip.id === detailTripId
  );
  const activeFilterCount =
    Number(Boolean(status)) +
    Number(Boolean(dateFrom)) +
    Number(Boolean(dateTo));
  const hasDiscoveryFilters = Boolean(
    search.trim() || status || dateFrom || dateTo
  );

  const closeTripDetail = () => {
    setDetailTripId(null);
    if (searchParams.has("tripId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("tripId");
      setSearchParams(next, { replace: true });
    }
  };

  const beginAction = (key: string) => {
    if (actionLocks.current.has(key)) return false;
    actionLocks.current.add(key);
    setActionError(null);
    setActionStatus(null);
    return true;
  };

  const finishAction = (key: string) => {
    actionLocks.current.delete(key);
  };

  const handleArchive = (tripId: string) => {
    const key = `archive:${tripId}`;
    if (!beginAction(key)) return;
    archive.mutate(tripId, {
      onSuccess: () => {
        setActionStatus("Trip archived.");
        toast.success("Trip archived.");
      },
      onError: () => {
        setActionError("Trip could not be archived. Please retry.");
        toast.error("Failed to archive trip.");
      },
      onSettled: () => finishAction(key),
    });
  };

  const handleUnassign = (tripId: string) => {
    const key = `unassign:${tripId}`;
    if (!beginAction(key)) return;
    unassign.mutate(tripId, {
      onSuccess: () => {
        setActionStatus("Operator unassigned.");
        toast.success("Operator unassigned.");
      },
      onError: () => {
        setActionError("Operator could not be unassigned. Please retry.");
        toast.error("Failed to unassign operator.");
      },
      onSettled: () => finishAction(key),
    });
  };

  const handleStart = (tripId: string) => {
    const key = `start:${tripId}`;
    if (!beginAction(key)) return;
    startTrip.mutate(tripId, {
      onSuccess: () => {
        setActionStatus("Trip started.");
        toast.success("Trip started.");
      },
      onError: () => {
        setActionError("Trip could not be started. Please retry.");
        toast.error("Failed to start trip.");
      },
      onSettled: () => finishAction(key),
    });
  };

  const handleDelete = (tripId: string) => {
    const key = `delete:${tripId}`;
    if (!beginAction(key)) return;
    deleteTrip.mutate(tripId, {
      onSuccess: () => {
        setActionStatus("Trip deleted.");
        toast.success("Trip deleted.");
        setDeletingTrip(null);
        closeTripDetail();
      },
      onError: () => {
        setActionError("Trip could not be deleted. Please retry.");
        toast.error("Failed to delete trip.");
      },
      onSettled: () => finishAction(key),
    });
  };

  return (
    <div className="carrier-page">
      <PageHeader
        title="Trips"
        actions={
          <>
            <Button
              variant="outline"
              size="touch"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button size="touch" onClick={() => setCreating(true)}>
              <Plus className="w-4 h-4" />
              Create trip
            </Button>
          </>
        }
      />

      {actionError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {actionError}
        </div>
      )}
      {actionStatus && (
        <div
          role="status"
          className="rounded-md border bg-muted/40 p-3 text-sm"
        >
          {actionStatus}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="border-b p-4">
          <TableToolbar
            className="mb-0"
            search={search}
            onSearchChange={(value) => {
              resetPage();
              setSearch(value);
            }}
            searchPlaceholder="Trip #, operator, vehicle..."
            searchAriaLabel="Search trips"
          >
            <div className="hidden xl:block">
              <TripFilterFields
                status={status}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onStatusChange={(value) => {
                  resetPage();
                  setStatus(value);
                }}
                onDateFromChange={(value) => {
                  resetPage();
                  setDateFrom(value);
                }}
                onDateToChange={(value) => {
                  resetPage();
                  setDateTo(value);
                }}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="w-full sm:w-auto xl:hidden"
              onClick={() => setFiltersOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
          </TableToolbar>
        </div>

        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetContent
            side="right"
            className="flex w-full flex-col p-0 sm:max-w-sm"
          >
            <SheetHeader className="border-b px-4 py-4 pr-12 text-left">
              <SheetTitle>Trip filters</SheetTitle>
              <SheetDescription>
                Narrow the carrier-visible Trip list.
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto p-4">
              <TripFilterFields
                mobile
                status={status}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onStatusChange={(value) => {
                  resetPage();
                  setStatus(value);
                }}
                onDateFromChange={(value) => {
                  resetPage();
                  setDateFrom(value);
                }}
                onDateToChange={(value) => {
                  resetPage();
                  setDateTo(value);
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-2 border-t p-4">
              <Button
                type="button"
                variant="outline"
                size="touch"
                disabled={activeFilterCount === 0}
                onClick={() => {
                  setStatus("");
                  setDateFrom("");
                  setDateTo("");
                  resetPage();
                }}
              >
                Clear filters
              </Button>
              <Button
                type="button"
                size="touch"
                onClick={() => setFiltersOpen(false)}
              >
                Show trips
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        {error && data && (
          <div
            role="alert"
            className="border-b border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            {hasHttpStatus(error, 403)
              ? "You do not have permission to refresh trips. Cached trips remain visible."
              : "Trips could not be refreshed. Cached trips remain visible."}
          </div>
        )}

        <div className="divide-y md:hidden">
          {isLoading && (
            <div
              role="status"
              aria-label="Loading trips"
              className="space-y-2 p-3"
            >
              {["trip-skeleton-1", "trip-skeleton-2", "trip-skeleton-3"].map(
                (key) => (
                  <div
                    key={key}
                    className="h-24 animate-pulse rounded-md bg-muted"
                  />
                )
              )}
            </div>
          )}
          {error && !data && (
            <div
              role="alert"
              className="space-y-3 p-4 text-sm text-destructive"
            >
              <p>
                {hasHttpStatus(error, 403)
                  ? "You do not have permission to view trips."
                  : "Failed to load trips."}
              </p>
              {!hasHttpStatus(error, 403) && (
                <Button
                  variant="outline"
                  size="touch"
                  onClick={() => refetch()}
                >
                  Retry
                </Button>
              )}
            </div>
          )}
          {showRows && rows.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {hasDiscoveryFilters
                ? "No trips match your filters."
                : "No trips yet."}
            </div>
          )}
          {showRows &&
            rows.map((trip) => (
              <article key={trip.id} className="relative p-3">
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`View trip TRP-${trip.tripNumber}`}
                  className="absolute inset-0 h-full w-full rounded-none p-0"
                  onClick={() => setDetailTripId(trip.id)}
                />
                <div className="pointer-events-none relative space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full border"
                        style={{ backgroundColor: trip.color ?? "transparent" }}
                        aria-hidden="true"
                      />
                      <span className="font-mono text-sm font-medium">
                        TRP-{trip.tripNumber}
                      </span>
                    </div>
                    <StatusBadge status={trip.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <span className="truncate">{driverName(trip)}</span>
                    <span className="truncate text-right">
                      {vehicleLabel(trip)}
                    </span>
                    <span className="text-muted-foreground">
                      {trip.waypoints?.length ?? "Unknown"} stops
                    </span>
                    <span className="text-right text-muted-foreground">
                      {tripDateLabel(trip)}
                    </span>
                  </div>
                  <TripProgress progress={trip} />
                </div>
              </article>
            ))}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" aria-label="Color" />
                <TableHead className="whitespace-nowrap">Trip ID</TableHead>
                <TableHead className="whitespace-nowrap">
                  Dispatch Center
                </TableHead>
                <TableHead className="whitespace-nowrap">Operator</TableHead>
                <TableHead className="whitespace-nowrap">Vehicle</TableHead>
                <TableHead className="whitespace-nowrap">Stops</TableHead>
                <TableHead className="whitespace-nowrap">Tasks</TableHead>
                <TableHead className="whitespace-nowrap">Status</TableHead>
                <TableHead className="whitespace-nowrap">Date</TableHead>
                <TableHead className="w-12" aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <LoadingRows columns={COLUMNS} />}
              {error && !data && (
                <ErrorRow
                  columns={COLUMNS}
                  message={
                    hasHttpStatus(error, 403)
                      ? "You do not have permission to view trips."
                      : "Failed to load trips."
                  }
                  onRetry={
                    hasHttpStatus(error, 403) ? undefined : () => refetch()
                  }
                />
              )}
              {showRows && rows.length === 0 && (
                <EmptyRow columns={COLUMNS}>
                  {hasDiscoveryFilters
                    ? "No trips match your filters."
                    : "No trips yet."}
                </EmptyRow>
              )}
              {showRows &&
                rows.map((trip) => (
                  <TableRow
                    key={trip.id}
                    className="cursor-pointer"
                    tabIndex={0}
                    aria-label={`View trip TRP-${trip.tripNumber}`}
                    onClick={() => setDetailTripId(trip.id)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setDetailTripId(trip.id);
                      }
                    }}
                  >
                    <TableCell>
                      <span
                        className="inline-block h-3 w-3 rounded-full border"
                        style={{ backgroundColor: trip.color ?? "transparent" }}
                        title="Trip color"
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">
                      TRP-{trip.tripNumber}
                    </TableCell>
                    <TableCell>{dispatchCenterName(trip)}</TableCell>
                    <TableCell>{driverName(trip)}</TableCell>
                    <TableCell>{vehicleLabel(trip)}</TableCell>
                    <TableCell>{trip.waypoints?.length ?? "Unknown"}</TableCell>
                    <TableCell>
                      <TripProgress progress={trip} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={trip.status} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {tripDateLabel(trip)}
                    </TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <TripActionsMenu
                        trip={trip}
                        onView={() => setDetailTripId(trip.id)}
                        onAssignTasks={() => setAssignTasksTripId(trip.id)}
                        onAssignOperator={() => setAssignTrip(trip)}
                        onAssignVehicle={() => setVehicleTrip(trip)}
                        onStart={() => handleStart(trip.id)}
                        onUnassign={() => handleUnassign(trip.id)}
                        onEditColor={() => setColorTrip(trip)}
                        onArchive={() => handleArchive(trip.id)}
                        onDelete={() => setDeletingTrip(trip)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {data && (
          <div className="border-t p-3">
            <TablePager
              page={data.metadata.page}
              pageSize={data.metadata.limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        )}
      </Card>

      <CreateTripDialog
        open={creating}
        onOpenChange={setCreating}
        dialogPresentation="responsive-sheet"
      />

      {assignTrip && (
        <AssignOperatorDialog
          open={!!assignTrip}
          onOpenChange={(open) => {
            if (!open) setAssignTrip(null);
          }}
          trip={assignTrip}
          dialogPresentation="responsive-sheet"
        />
      )}

      {colorTrip && (
        <EditTripColorDialog
          open={!!colorTrip}
          onOpenChange={(open) => {
            if (!open) setColorTrip(null);
          }}
          tripId={colorTrip.id}
          initialColor={colorTrip.color}
          dialogPresentation="responsive-sheet"
        />
      )}

      {vehicleTrip && (
        <AssignVehicleDialog
          open={!!vehicleTrip}
          onOpenChange={(open) => {
            if (!open) setVehicleTrip(null);
          }}
          trip={vehicleTrip}
          dialogPresentation="responsive-sheet"
        />
      )}

      {detailTripId && (
        <TripDetailDialog
          open={!!detailTripId}
          onOpenChange={(open) => {
            if (!open) closeTripDetail();
          }}
          tripId={detailTripId}
          summary={selectedTrip}
          onAssignTasks={() => {
            closeTripDetail();
            setAssignTasksTripId(detailTripId);
          }}
          onAssignOperator={() => {
            closeTripDetail();
            if (selectedTrip) setAssignTrip(selectedTrip);
          }}
          onAssignVehicle={(trip) => {
            closeTripDetail();
            setVehicleTrip(trip);
          }}
          onEditColor={(trip) => {
            closeTripDetail();
            setColorTrip(trip);
          }}
          onStart={() => handleStart(detailTripId)}
          onUnassign={() => handleUnassign(detailTripId)}
          onArchive={() => handleArchive(detailTripId)}
          onDelete={(trip) => setDeletingTrip(trip)}
        />
      )}

      {assignTasksTripId && (
        <AssignTasksDialog
          open={!!assignTasksTripId}
          onOpenChange={(open) => {
            if (!open) setAssignTasksTripId(null);
          }}
          tripId={assignTasksTripId}
          dialogPresentation="responsive-sheet"
        />
      )}

      <AlertDialog
        open={!!deletingTrip}
        onOpenChange={(open) => {
          if (!open && !deleteTrip.isPending) setDeletingTrip(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete trip?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete {deletingTrip ? `TRP-${deletingTrip.tripNumber}` : "trip"}{" "}
              and remove its task assignments? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTrip.isPending}>
              Keep trip
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteTrip.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (deletingTrip) handleDelete(deletingTrip.id);
              }}
            >
              {deleteTrip.isPending ? "Deleting…" : "Delete trip"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TripFilterFields({
  status,
  dateFrom,
  dateTo,
  onStatusChange,
  onDateFromChange,
  onDateToChange,
  mobile = false,
}: {
  status: string;
  dateFrom: string;
  dateTo: string;
  onStatusChange: (value: string) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  mobile?: boolean;
}) {
  const fieldSuffix = mobile ? "mobile" : "desktop";
  return (
    <div className={mobile ? "space-y-4" : "flex items-center gap-2"}>
      <label
        htmlFor={`trip-status-filter-${fieldSuffix}`}
        className={mobile ? "block space-y-1" : "block"}
      >
        <span className={mobile ? "text-sm font-medium" : "sr-only"}>
          Status
        </span>
        <NativeSelect
          id={`trip-status-filter-${fieldSuffix}`}
          aria-label="Filter by status"
          className={mobile ? "w-full" : "w-40"}
          value={status}
          onChange={(event) => onStatusChange(event.target.value)}
        >
          <option value="">All statuses</option>
          {STATUS_FILTERS.map((option) => (
            <option key={option} value={option}>
              {humanizeStatus(option)}
            </option>
          ))}
        </NativeSelect>
      </label>
      <label
        htmlFor={`trip-date-from-${fieldSuffix}`}
        className={mobile ? "block space-y-1" : "block"}
      >
        <span className={mobile ? "text-sm font-medium" : "sr-only"}>
          From date
        </span>
        <Input
          id={`trip-date-from-${fieldSuffix}`}
          type="date"
          aria-label="Filter from date"
          className={mobile ? undefined : "w-40"}
          value={dateFrom}
          max={dateTo || undefined}
          onChange={(event) => onDateFromChange(event.target.value)}
        />
      </label>
      <label
        htmlFor={`trip-date-to-${fieldSuffix}`}
        className={mobile ? "block space-y-1" : "block"}
      >
        <span className={mobile ? "text-sm font-medium" : "sr-only"}>
          To date
        </span>
        <Input
          id={`trip-date-to-${fieldSuffix}`}
          type="date"
          aria-label="Filter to date"
          className={mobile ? undefined : "w-40"}
          value={dateTo}
          min={dateFrom || undefined}
          onChange={(event) => onDateToChange(event.target.value)}
        />
      </label>
    </div>
  );
}

function TripActionsMenu({
  trip,
  onView,
  onAssignTasks,
  onAssignOperator,
  onAssignVehicle,
  onStart,
  onUnassign,
  onEditColor,
  onArchive,
  onDelete,
}: {
  trip: CarrierTrip;
  onView: () => void;
  onAssignTasks: () => void;
  onAssignOperator: () => void;
  onAssignVehicle: () => void;
  onStart: () => void;
  onUnassign: () => void;
  onEditColor: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  return (
    <RowMenu label={`Actions for TRP-${trip.tripNumber}`}>
      <RowMenuItem onClick={onView}>View detail</RowMenuItem>
      <RowMenuItem onClick={onAssignTasks}>Assign tasks</RowMenuItem>
      <RowMenuItem onClick={onAssignOperator}>Assign operator</RowMenuItem>
      <RowMenuItem onClick={onAssignVehicle}>Assign vehicle</RowMenuItem>
      {trip.status === "PLANNED" && trip.operatorId && (
        <RowMenuItem onClick={onStart}>Start trip</RowMenuItem>
      )}
      {trip.operatorId && (
        <RowMenuItem onClick={onUnassign}>Unassign operator</RowMenuItem>
      )}
      <RowMenuItem onClick={onEditColor}>Edit color</RowMenuItem>
      {trip.status === "COMPLETED" && (
        <RowMenuItem onClick={onArchive}>Archive</RowMenuItem>
      )}
      {trip.status === "PLANNED" && (
        <RowMenuItem variant="destructive" onClick={onDelete}>
          Delete
        </RowMenuItem>
      )}
    </RowMenu>
  );
}
